import { CommonResponse } from "@pinnacle/types/request.wrapper";
import axios from "axios";
import { useUserStore } from "@pinnacle/store/user";
import * as Sentry from "@sentry/react";

const instance = axios.create({
  baseURL: import.meta.env.VITE_BASE_API_URL,
  timeout: 10000,
});

instance.interceptors.request.use(
  (config: any) => {
    const token = useUserStore.getState().auth?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    Sentry.captureException(error);

    if (error.response?.status) {
      switch (error.response.status) {
        case 401: {
          //unauthorized
          console.log("[Request]未登陆", error.response.data);
          useUserStore.getState().clearLoginInfo();
          // 避免在登录页内触发 401 时反复重定向
          const pathname = window.location.pathname;
          if (pathname !== "/login" && !pathname.startsWith("/login/")) {
            window.location.href =
              "/login?from=" +
              encodeURIComponent(pathname + window.location.search);
            // 已经在跳登录页了，返回永不 settle 的 Promise，
            // 避免下游 onError 在页面卸载前再弹一次无意义的错误提示
            return new Promise(() => {});
          }
          break;
        }
        case 400: {
          console.log("[Request]业务错误", error.response.data);
          break;
        }
        case 402: {
          console.log("[Request]您的账号已被禁用", error.response.data);
          break;
        }
        case 403: {
          //forbidden
          // 共享包内不引入 UI 组件库，交由业务层通过 reject 自行提示；
          // 后端 403 的 error 常为空数组，这里兜底一条可读文案
          console.log("[Request]访问未经授权的资源", error.response.data);
          if (
            error.response.data &&
            typeof error.response.data === "object" &&
            !error.response.data.message &&
            !(error.response.data.error?.length > 0)
          ) {
            error.response.data.message = "无权限访问";
          }
          break;
        }
        case 500: {
          console.log("[Request]internal server error", error.response.data);
          break;
        }
        default: {
          console.log(
            "[Request]请求正常返回，但是出现未知错误",
            error.response.data ||
              error.response.data.message ||
              error.response.data.toString() ||
              error.message
          );
        }
      }
    } else {
      console.log(
        "[Request]请求失败，未知错误",
        error.response?.data?.toString() || error.message || ""
      );
      return Promise.reject({
        code: 500000,
        message:
          error.response?.data?.toString() || error.message || "unsorted error",
      });
    }
    return Promise.reject(error.response.data);
  }
);

export function get<T>(
  url: string,
  params: any = null
): Promise<CommonResponse<T>> {
  return instance.get(url, {
    params,
  });
}

/**
 * post请求
 * @param {string} url     请求地址
 * @param {*} data    数据
 */
export function post<T>(url: string, data = {}): Promise<CommonResponse<T>> {
  return instance.post(url, data);
}

/**
 * put请求
 * @param {string} url     请求地址
 * @param {*} data    数据
 */
export function put<T>(url: string, data = {}): Promise<CommonResponse<T>> {
  return instance.put(url, data);
}

/**
 * patch请求
 * @param {string} url     请求地址
 * @param {*} data    数据
 */
export function patch<T>(url: string, data = {}): Promise<CommonResponse<T>> {
  return instance.patch(url, data);
}

/**
 * delete请求
 * @param {string} url     请求地址
 * @param {*} data    数据
 */
export function del<T>(url: string, data = {}): Promise<CommonResponse<T>> {
  return instance.delete(url, { data });
}

/**
 * post请求（支持透传 axios 配置，例如为耗时接口自定义超时时间）
 * @param {string} url     请求地址
 * @param {*} data    数据
 * @param {*} config  axios 请求配置
 */
export function postWithConfig<T>(
  url: string,
  data = {},
  config?: import("axios").AxiosRequestConfig
): Promise<CommonResponse<T>> {
  return instance.post(url, data, config);
}
